'use client';

import { useEffect } from 'react';

import { initOpenReplay, setUserMetadata } from '../../lib/openreplay';
import { getBrowserFingerprint, getUserId } from '../../lib/user-id';

/**
 * OpenReplay Provider Component
 * Initializes OpenReplay tracking when mounted and sets initial anonymous user metadata
 *
 * Flow:
 * 1. On first visit: Generate UUID and store in localStorage
 * 2. Send UUID to OpenReplay as userId (even for anonymous users)
 * 3. On signup: Keep same UUID, add authenticated user metadata
 * 4. On signin: Use UUID from database, overwrite localStorage if different
 */
export function OpenReplayProvider({
  children
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    // Initialize OpenReplay on mount
    initOpenReplay();

    // Get or generate persistent user ID (UUID)
    // This will either retrieve existing UUID from localStorage or generate a new one
    const userId = getUserId();
    const fingerprint = getBrowserFingerprint();

    // Set initial metadata with the UUID as userId
    // This UUID will be used throughout the user's journey
    setUserMetadata({
      userId, // The persistent UUID - used for both anonymous and authenticated users
      userType: 'anonymous',
      ...fingerprint
    });
  }, []);

  return <>{children}</>;
}
